import React, {useCallback, useEffect, useRef, useState} from "react";
import Panzoom from "@panzoom/panzoom/dist/panzoom.js";

export const MINIMUM_VIEWPORT_SPAN_MULTIPLIER = 2;
export const DEFAULT_MAXIMUM_ZOOM_MULTIPLIER = 2.5;
export const MAXIMUM_CANVAS_BACKING_PIXELS = 16000000;

const ZOOM_STEP = 0.5;

export const getMinimumZoomScale = (stageWidth, stageHeight, imageWidth, imageHeight) => {
    if (stageWidth >= stageHeight) {
        return (stageWidth * MINIMUM_VIEWPORT_SPAN_MULTIPLIER) / imageWidth;
    }

    return (stageHeight * MINIMUM_VIEWPORT_SPAN_MULTIPLIER) / imageHeight;
};

export const getInitialPanPosition = (stageWidth, stageHeight, imageWidth, imageHeight, scale) => ({
    x: (stageWidth - imageWidth) / (2 * scale),
    y: (stageHeight - imageHeight) / (2 * scale),
});

export const getCanvasBackingDimensions = (
    sourceWidth,
    sourceHeight,
    fittedWidth,
    fittedHeight,
    minimumZoomScale,
    devicePixelRatio = 1,
) => {
    if (
        !sourceWidth || !sourceHeight || !fittedWidth || !fittedHeight
        || !minimumZoomScale || !Number.isFinite(devicePixelRatio)
    ) {
        return false;
    }

    const targetScale = Math.max(1, devicePixelRatio) * minimumZoomScale;
    const requestedWidth = Math.min(sourceWidth, Math.round(fittedWidth * targetScale));
    const requestedHeight = Math.min(sourceHeight, Math.round(fittedHeight * targetScale));
    const requestedPixels = requestedWidth * requestedHeight;
    const capScale = requestedPixels > MAXIMUM_CANVAS_BACKING_PIXELS
        ? Math.sqrt(MAXIMUM_CANVAS_BACKING_PIXELS / requestedPixels)
        : 1;

    return {
        width: Math.max(1, Math.floor(requestedWidth * capScale)),
        height: Math.max(1, Math.floor(requestedHeight * capScale)),
    };
};

const preventDefault = (event) => {
    event.preventDefault();
};

const ProtectedImageViewer = ({
    imageUrl,
    imageName,
    onClose,
    maximumZoomMultiplier = DEFAULT_MAXIMUM_ZOOM_MULTIPLIER,
}) => {
    const stageRef = useRef(null);
    const canvasRef = useRef(null);
    const sourceImageRef = useRef(null);
    const panzoomRef = useRef(null);
    const wheelHandlerRef = useRef(null);
    const containmentTimerRef = useRef(null);
    const [imageLoadState, setImageLoadState] = useState("loading");

    const destroyPanzoom = useCallback(() => {
        const stage = stageRef.current;
        const panzoom = panzoomRef.current;

        if (containmentTimerRef.current) {
            window.clearTimeout(containmentTimerRef.current);
        }
        containmentTimerRef.current = null;

        if (stage && wheelHandlerRef.current) {
            stage.removeEventListener("wheel", wheelHandlerRef.current);
        }
        wheelHandlerRef.current = null;

        if (panzoom) {
            panzoom.destroy();
        }
        panzoomRef.current = null;
    }, []);

    const fitCanvasToStage = useCallback(() => {
        const stage = stageRef.current;
        const canvas = canvasRef.current;
        const sourceImage = sourceImageRef.current;

        if (!stage || !canvas || !sourceImage || !sourceImage.naturalWidth || !sourceImage.naturalHeight) {
            return false;
        }

        const scale = Math.min(
            stage.clientWidth / sourceImage.naturalWidth,
            stage.clientHeight / sourceImage.naturalHeight,
        );

        if (!scale || !Number.isFinite(scale)) {
            return false;
        }

        const width = Math.round(sourceImage.naturalWidth * scale);
        const height = Math.round(sourceImage.naturalHeight * scale);
        const minimumZoomScale = getMinimumZoomScale(
            stage.clientWidth,
            stage.clientHeight,
            width,
            height,
        );
        const backingDimensions = getCanvasBackingDimensions(
            sourceImage.naturalWidth,
            sourceImage.naturalHeight,
            width,
            height,
            minimumZoomScale,
            window.devicePixelRatio || 1,
        );
        const context = canvas.getContext("2d");

        if (!backingDimensions || !context) {
            return false;
        }

        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        canvas.width = backingDimensions.width;
        canvas.height = backingDimensions.height;
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(sourceImage, 0, 0, canvas.width, canvas.height);

        return {width, height, minimumZoomScale};
    }, []);

    const enableContainmentAfterInitialPosition = useCallback((panzoom) => {
        if (containmentTimerRef.current) {
            window.clearTimeout(containmentTimerRef.current);
        }

        containmentTimerRef.current = window.setTimeout(() => {
            if (panzoomRef.current === panzoom) {
                panzoom.setOptions({contain: "outside"});
            }
        });
    }, []);

    const initializePanzoom = useCallback(() => {
        const stage = stageRef.current;
        const canvas = canvasRef.current;

        if (!stage || !canvas) {
            return;
        }

        const fittedImage = fitCanvasToStage();
        if (!fittedImage) {
            setImageLoadState("error");
            return;
        }

        destroyPanzoom();

        const startPosition = getInitialPanPosition(
            stage.clientWidth,
            stage.clientHeight,
            fittedImage.width,
            fittedImage.height,
            fittedImage.minimumZoomScale,
        );
        const panzoom = Panzoom(canvas, {
            canvas: true,
            maxScale: fittedImage.minimumZoomScale * maximumZoomMultiplier,
            minScale: fittedImage.minimumZoomScale,
            startX: startPosition.x,
            startY: startPosition.y,
            startScale: fittedImage.minimumZoomScale,
            step: ZOOM_STEP,
        });
        const wheelHandler = (event) => {
            event.preventDefault();
            panzoom.zoomWithWheel(event);
        };

        stage.addEventListener("wheel", wheelHandler, {passive: false});
        panzoomRef.current = panzoom;
        wheelHandlerRef.current = wheelHandler;
        enableContainmentAfterInitialPosition(panzoom);
    }, [destroyPanzoom, enableContainmentAfterInitialPosition, fitCanvasToStage, maximumZoomMultiplier]);

    useEffect(() => {
        let cancelled = false;

        destroyPanzoom();
        sourceImageRef.current = null;
        setImageLoadState("loading");

        if (!imageUrl) {
            return undefined;
        }

        const sourceImage = new Image();

        sourceImage.onload = () => {
            if (cancelled) {
                return;
            }

            sourceImageRef.current = sourceImage;
            setImageLoadState("ready");
        };
        sourceImage.onerror = () => {
            if (!cancelled) {
                setImageLoadState("error");
            }
        };
        sourceImage.src = imageUrl;

        return () => {
            cancelled = true;
            sourceImage.onload = null;
            sourceImage.onerror = null;
        };
    }, [destroyPanzoom, imageUrl]);

    useEffect(() => {
        if (imageLoadState !== "ready") {
            return undefined;
        }

        initializePanzoom();
        return destroyPanzoom;
    }, [destroyPanzoom, imageLoadState, initializePanzoom]);

    useEffect(() => {
        const resetForViewportChange = () => {
            const stage = stageRef.current;
            const fittedImage = fitCanvasToStage();

            if (!panzoomRef.current || !stage || !fittedImage) {
                return;
            }

            const startPosition = getInitialPanPosition(
                stage.clientWidth,
                stage.clientHeight,
                fittedImage.width,
                fittedImage.height,
                fittedImage.minimumZoomScale,
            );
            panzoomRef.current.setOptions({
                contain: false,
                maxScale: fittedImage.minimumZoomScale * maximumZoomMultiplier,
                minScale: fittedImage.minimumZoomScale,
                startX: startPosition.x,
                startY: startPosition.y,
                startScale: fittedImage.minimumZoomScale,
            });
            panzoomRef.current.reset({animate: false});
            enableContainmentAfterInitialPosition(panzoomRef.current);
        };

        window.addEventListener("resize", resetForViewportChange);
        window.addEventListener("orientationchange", resetForViewportChange);

        return () => {
            window.removeEventListener("resize", resetForViewportChange);
            window.removeEventListener("orientationchange", resetForViewportChange);
        };
    }, [enableContainmentAfterInitialPosition, fitCanvasToStage, maximumZoomMultiplier]);

    useEffect(() => {
        const closeOnEscape = (event) => {
            if (event.key === "Escape") {
                event.preventDefault();
                onClose();
            }
        };

        window.addEventListener("keydown", closeOnEscape);
        return () => {
            window.removeEventListener("keydown", closeOnEscape);
        };
    }, [onClose]);

    if (!imageUrl) {
        return null;
    }

    const zoomIn = () => {
        if (panzoomRef.current) {
            panzoomRef.current.zoomIn();
        }
    };

    const zoomOut = () => {
        if (panzoomRef.current) {
            panzoomRef.current.zoomOut();
        }
    };

    const resetZoom = () => {
        if (panzoomRef.current) {
            panzoomRef.current.reset();
        }
    };

    return (
        <div
            className="customer-image-viewer"
            onContextMenu={preventDefault}
            role="dialog"
            aria-modal="true"
            aria-label={imageName ? `${imageName} képnézegető` : "Képnézegető"}
        >
            <div
                className="customer-image-viewer-stage"
                ref={stageRef}
                onContextMenu={preventDefault}
            >
                <canvas
                    className="customer-image-viewer-image"
                    ref={canvasRef}
                    role="img"
                    aria-label={imageName || "Galéria kép"}
                />
                {imageLoadState === "loading" && (
                    <p className="customer-image-viewer-status" role="status">Kép betöltése…</p>
                )}
                {imageLoadState === "error" && (
                    <p className="customer-image-viewer-status customer-image-viewer-status-error" role="alert">
                        A kép betöltése nem sikerült.
                    </p>
                )}
            </div>

            <button
                type="button"
                onClick={onClose}
                className="customer-image-viewer-back btn btn-simple btn-info btn-icon"
                aria-label="Vissza a galériához"
            >
                <i className="material-icons material-icons--outlined">navigate_before</i>
            </button>
            <div className="customer-image-viewer-controls" role="group" aria-label="Nagyítás vezérlők">
                <button
                    type="button"
                    onClick={zoomOut}
                    className="btn btn-simple btn-info btn-icon"
                    aria-label="Kicsinyítés"
                >
                    <i className="material-icons material-icons--outlined">remove</i>
                </button>
                <button
                    type="button"
                    onClick={resetZoom}
                    className="btn btn-simple btn-info btn-icon"
                    aria-label="Nagyítás visszaállítása a minimális értékre"
                >
                    <i className="material-icons material-icons--outlined">center_focus_strong</i>
                </button>
                <button
                    type="button"
                    onClick={zoomIn}
                    className="btn btn-simple btn-info btn-icon"
                    aria-label="Nagyítás"
                >
                    <i className="material-icons material-icons--outlined">add</i>
                </button>
            </div>
            <button
                type="button"
                onClick={onClose}
                className="customer-image-viewer-close btn btn-simple btn-danger btn-icon"
                aria-label="Képnézegető bezárása"
            >
                <i className="material-icons material-icons--outlined">close</i>
            </button>
        </div>
    );
};

export default ProtectedImageViewer;